/**
 * fabStack — pure ordering + collapse logic for the FabStack registry (#1696).
 *
 * DECIDED (2026-07-27, issue #1696 comment): everything except the debrief
 * coach FAB collapses into ONE fan-open, tap-based menu. The coach FAB keeps
 * its own corner (bottom-right, the most reachable spot) and its attention
 * badge, and is never a member of the menu's registrant list.
 *
 * Corner-aware (this is the actual fix over the mega62 predecessor, whose own
 * header used to say "Scope: bottom-right only... a separate, deliberately-
 * shipped [top-left] cluster... untouched by this module" — that scoping is
 * exactly what left the top-left dock uncoordinated and is why this module no
 * longer hardcodes a single corner): FabStackContext.jsx now keys its
 * registry by an arbitrary `corner` string. This module stays corner-agnostic
 * on purpose — it only orders whatever `activeSlots` map it's handed, so it
 * works identically for the menu's corner (several registrants) and the
 * coach's corner (always exactly one).
 *
 * Two corners are wired up today:
 *   - 'menu'  — the consolidated fan-open FAB menu, docked at the existing
 *     top-left chat dock (#1567/#1170 placement, unchanged — only the
 *     PLACEMENT is frozen, the internal mechanism moved from a hardcoded
 *     +56px offset hack to this same priority-ordered stack). Registrants:
 *     home, help, bugReport, askPraxis.
 *   - 'coach' — the debrief coach FAB's own corner (bottom-right). Exactly
 *     one registrant ('coach'), so ordering is trivial (always index 0), but
 *     it goes through the same registry so a future second bottom-right FAB
 *     inherits real stacking instead of a new one-off.
 *
 * Priority within the 'menu' corner (lower number = nearer the trigger =
 * bottom-most/first slot in the fanned-out list):
 *   0 — HOME: the most time-critical exit affordance (leaving an active
 *       conversation), closest to the trigger.
 *   1 — HELP: global secondary utility.
 *   2 — BUG_REPORT: signed-in, non-guest learners only.
 *   3 — ASK_PRAXIS: facilitator-only, least frequently used of the four.
 * COACH keeps its own value (0) for the 'coach' corner, where it is always
 * the sole member — the number only matters as a stable, revisable single
 * source of truth if a second bottom-right registrant is ever added.
 *
 * This ordering is a single, easily-revisable source of truth — flip the
 * numbers here (and only here) if product wants a different arrangement.
 *
 * This module is deliberately React-free (mirrors lib/navigation/chatPhase.js)
 * so the ordering can be unit-tested with plain objects — no jsdom, no
 * @testing-library render, no React at all.
 */

export const FAB_PRIORITY = {
  HOME: 0,
  HELP: 1,
  BUG_REPORT: 2,
  ASK_PRAXIS: 3,
  COACH: 0,
};

/**
 * FAB_STACK_COLLAPSE_THRESHOLD — the "collapse-at-N" rule from the #1696
 * decision checklist ("Collapse-at-N rule in the stacker, so the next
 * floating button inherits the behaviour instead of reinventing it"): once a
 * single corner would need to display more than this many simultaneous
 * members, `shouldCollapseFabStack` reports true and the caller (FabMenu)
 * shows only the top N in the open fan and folds the remainder behind an
 * overflow toggle rather than growing the fan without bound.
 *
 * Set to 4 because that is the 'menu' corner's own full known membership
 * today (home/help/bugReport/askPraxis) — the real trigger condition is
 * "a FIFTH registrant shows up", which this module can only detect, never
 * predict, so the constant is deliberately the current ceiling rather than a
 * guess at a future one.
 */
export const FAB_STACK_COLLAPSE_THRESHOLD = 4;

/**
 * @param {Record<string, number>} activeSlots  id -> priority, for FABs
 *   currently eligible for a slot in ONE corner (their OWN visibility
 *   condition is true — this module only orders, it never decides visibility).
 * @returns {string[]} ids sorted bottom-most/nearest-the-trigger (index 0) to
 *   top-most/farthest, ties broken lexicographically by id for determinism.
 */
export function computeFabStackOrder(activeSlots) {
  return Object.entries(activeSlots || {})
    .sort(([idA, priorityA], [idB, priorityB]) => {
      if (priorityA !== priorityB) return priorityA - priorityB;
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    })
    .map(([id]) => id);
}

/**
 * @param {Record<string, number>} activeSlots
 * @param {string} id
 * @returns {number} zero-based stack position within its corner (0 = nearest
 *   the trigger/corner), or -1 if `id` is not a current member of `activeSlots`.
 */
export function computeFabStackIndex(activeSlots, id) {
  if (!activeSlots || !(id in activeSlots)) return -1;
  return computeFabStackOrder(activeSlots).indexOf(id);
}

/**
 * shouldCollapseFabStack — the collapse-at-N predicate. Pure count check
 * against however many ids are CURRENTLY active in one corner's slot map;
 * takes no opinion on which ids get folded (that ordering decision is
 * computeFabStackOrder's — the caller slices the first N of that list).
 *
 * @param {Record<string, number>} activeSlots
 * @param {number} [threshold] defaults to FAB_STACK_COLLAPSE_THRESHOLD
 * @returns {boolean}
 */
export function shouldCollapseFabStack(activeSlots, threshold = FAB_STACK_COLLAPSE_THRESHOLD) {
  return Object.keys(activeSlots || {}).length > threshold;
}
